import { Stack, Typography } from '@mui/material';
import { type ObjectLiteral, useSubmission, useUpdateUwQuestions } from 'api/submission';
import { APPLICATION_STEPS, EC_BUSINESS_CONTACT_FIELDS } from 'common/constants';
import { displayBackendErrorMessage } from 'components/DisplayBackEndErrorMessage';
import { QuestionEngine, useQuestionEngine } from 'components/lib/QuestionEngine';
import { StepFooterActions } from 'components/StepFooterActions';
import { isEmpty, isEqual, pick } from 'lodash-es';
import { type FC, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useFullscreenLoader } from 'store/features/UI';

const CONTACT_FIELD_CODES: string[] = EC_BUSINESS_CONTACT_FIELDS.map((field) => field.code);

/**
 * The "About you" step on the EC rail. The fields come from the frontend, not from the question
 * config, so the step does not depend on a `business_contact` group in any config version.
 */
const BusinessContact: FC = () => {
  const { id } = useParams() as { id: string };
  const { data: submission } = useSubmission(id);
  const { mutateAsync: saveUnderwritingQuestions } = useUpdateUwQuestions(id);
  const { setFullscreenLoading } = useFullscreenLoader();
  const { validateFormik, updateState, ...questionEngineProps } = useQuestionEngine({
    fields: EC_BUSINESS_CONTACT_FIELDS,
    wrapperSx: { mb: 3 },
  });
  const { state } = questionEngineProps;

  const submissionState: ObjectLiteral = {
    ...submission?.data.policy_data,
    ...submission?.data.uw_questions,
  };

  useEffect(() => {
    if (!isEmpty(submission?.data)) {
      updateState(submissionState);
    }
  }, [submission?.data]);

  const handleNext = async (): Promise<boolean> => {
    const isValid = await validateFormik();

    if (!isValid) {
      return false;
    }

    if (isEqual(pick(state, CONTACT_FIELD_CODES), pick(submissionState, CONTACT_FIELD_CODES))) {
      return true;
    }

    try {
      setFullscreenLoading(true, 'Saving the answers');

      await saveUnderwritingQuestions({ id, body: pick(state, CONTACT_FIELD_CODES) });

      return true;
    } catch (_error) {
      displayBackendErrorMessage(_error);
      return false;
    } finally {
      setFullscreenLoading(false);
    }
  };

  return (
    <Stack sx={{ flex: 1 }}>
      <Typography variant="h1" sx={{ mb: 3 }}>
        {APPLICATION_STEPS.business_contact.label}
      </Typography>

      <QuestionEngine {...questionEngineProps} />

      <StepFooterActions onNext={handleNext} />
    </Stack>
  );
};

export default BusinessContact;
